import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/lib/i18n";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import HelpBot from "@/components/HelpBot";
import Lightbox from "@/components/Lightbox";
import Privacy from "@/pages/Privacy";
import Sell from "@/pages/Sell";
import Marketplace from "@/pages/Marketplace";
import UserListings from "@/pages/UserListings";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Feed from "@/pages/Feed";
import Discover from "@/pages/Discover";
import Profile from "@/pages/Profile";
import EditProfile from "@/pages/EditProfile";
import Bookings from "@/pages/Bookings";
import Meet from "@/pages/Meet";
import Messages from "@/pages/Messages";
import PostCreate from "@/pages/PostCreate";
import AuthCallback from "@/pages/AuthCallback";
import BookingSuccess from "@/pages/BookingSuccess";
import Checkout from "@/pages/Checkout";
import Promote from "@/pages/Promote";
import Shop from "@/pages/Shop";
import ProductView from "@/pages/ProductView";
import OrderSuccess from "@/pages/OrderSuccess";
import GuestOrderAccess from "@/pages/GuestOrderAccess";
import GuestBookingAccess from "@/pages/GuestBookingAccess";
import TipSuccess from "@/pages/TipSuccess";
import { CharitiesList, CharityCreate, CharityView } from "@/pages/Charities";
import { VolunteeringList, VolunteerCreate, VolunteerView } from "@/pages/Volunteering";
import Orders from "@/pages/Orders";
import PayoutSettings from "@/pages/PayoutSettings";
import Jobs from "@/pages/Jobs";
import JobView from "@/pages/JobView";
import JobCreate from "@/pages/JobCreate";
import JobsMine from "@/pages/JobsMine";
import JobApplications from "@/pages/JobApplications";
import Market from "@/pages/Market";
import MarketView from "@/pages/MarketView";
import MarketCreate from "@/pages/MarketCreate";
import MarketMine from "@/pages/MarketMine";
import MarketSuccess from "@/pages/MarketSuccess";
import Rentals from "@/pages/Rentals";
import RentalView from "@/pages/RentalView";
import RentalCreate from "@/pages/RentalCreate";
import RentalsMine, { RentalBookings } from "@/pages/RentalsMine";
import RentalSuccess from "@/pages/RentalSuccess";
import Dashboard from "@/pages/Dashboard";
import PublicAvailability from "@/pages/PublicAvailability";
import Groups from "@/pages/Groups";
import GroupsShell from "@/pages/GroupsShell";
import GroupCreate from "@/pages/GroupCreate";
import GroupView from "@/pages/GroupView";
import GroupJoinPage from "@/pages/GroupJoinPage";
import Collabs from "@/pages/Collabs";
import CollabCreate from "@/pages/CollabCreate";
import CollabView from "@/pages/CollabView";
import Meetups from "@/pages/Meetups";
import MeetupCreate from "@/pages/MeetupCreate";
import MeetupView from "@/pages/MeetupView";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-10 text-center text-[#8A8A8A]">…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return children;
}

function Shell() {
  const loc = useLocation();
  // CRITICAL: detect Emergent auth hash synchronously during render (not in useEffect),
  // so the AuthCallback runs BEFORE any /api/auth/me check fires.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (loc.hash?.includes("session_id=")) return <><Nav/><AuthCallback /></>;

  return (
    <>
      <Nav />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/availability/:id" element={<PublicAvailability />} />
        <Route path="/groups" element={<GroupsShell />} />
        <Route path="/groups/new" element={<Protected><GroupCreate /></Protected>} />
        <Route path="/groups/join/:token" element={<Protected><GroupJoinPage /></Protected>} />
        <Route path="/groups/:id" element={<GroupsShell />} />
        <Route path="/collabs" element={<Collabs />} />
        <Route path="/collabs/new" element={<Protected><CollabCreate /></Protected>} />
        <Route path="/collabs/:id" element={<CollabView />} />
        <Route path="/meetups" element={<Meetups />} />
        <Route path="/meetups/new" element={<Protected><MeetupCreate /></Protected>} />
        <Route path="/meetups/:id" element={<MeetupView />} />
        <Route path="/charities" element={<CharitiesList />} />
        <Route path="/charities/new" element={<Protected><CharityCreate /></Protected>} />
        <Route path="/charities/:id" element={<CharityView />} />
        <Route path="/volunteering" element={<VolunteeringList />} />
        <Route path="/volunteering/new" element={<Protected><VolunteerCreate /></Protected>} />
        <Route path="/volunteering/:id" element={<VolunteerView />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/discover" element={<Discover />} />
        <Route path="/u/:id" element={<Profile />} />
        <Route path="/u/:id/listings" element={<UserListings />} />
        <Route path="/profile/edit" element={<Protected><EditProfile /></Protected>} />
        <Route path="/bookings" element={<Protected><Bookings /></Protected>} />
        <Route path="/meet/:id" element={<Protected><Meet /></Protected>} />
        <Route path="/booking/success" element={<BookingSuccess />} />
        <Route path="/checkout/:bookingId" element={<Checkout />} />
        <Route path="/booking/access/:token" element={<GuestBookingAccess />} />
        <Route path="/tip/success" element={<TipSuccess />} />
        <Route path="/promote" element={<Protected><Promote /></Protected>} />
        <Route path="/promote/success" element={<Protected><Promote /></Protected>} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/:id" element={<ProductView />} />
        <Route path="/order/success" element={<OrderSuccess />} />
        <Route path="/order/access/:token" element={<GuestOrderAccess />} />
        <Route path="/orders" element={<Protected><Orders /></Protected>} />
        <Route path="/settings/payouts" element={<Protected><PayoutSettings /></Protected>} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/jobs/new" element={<Protected><JobCreate /></Protected>} />
        <Route path="/jobs/mine" element={<Protected><JobsMine /></Protected>} />
        <Route path="/jobs/applications" element={<Protected><JobApplications /></Protected>} />
        <Route path="/jobs/:id" element={<JobView />} />
        <Route path="/market" element={<Market />} />
        <Route path="/market/new" element={<Protected><MarketCreate /></Protected>} />
        <Route path="/market/mine" element={<Protected><MarketMine /></Protected>} />
        <Route path="/market/success" element={<Protected><MarketSuccess /></Protected>} />
        <Route path="/market/:id" element={<MarketView />} />
        <Route path="/rentals" element={<Rentals />} />
        <Route path="/rentals/new" element={<Protected><RentalCreate /></Protected>} />
        <Route path="/rentals/mine" element={<Protected><RentalsMine /></Protected>} />
        <Route path="/rentals/bookings" element={<Protected><RentalBookings /></Protected>} />
        <Route path="/rentals/success" element={<Protected><RentalSuccess /></Protected>} />
        <Route path="/rentals/:id" element={<RentalView />} />
        <Route path="/messages" element={<Protected><Messages /></Protected>} />
        <Route path="/messages/:otherId" element={<Protected><Messages /></Protected>} />
        <Route path="/post/new" element={<Protected><PostCreate /></Protected>} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/sell" element={<Sell />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/services" element={<Navigate to="/discover" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Footer />
      <HelpBot />
      <Lightbox />
    </>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <CurrencyProvider>
        <AuthProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
        </AuthProvider>
      </CurrencyProvider>
    </I18nProvider>
  );
}
